import LocationOnOutlined from "@mui/icons-material/LocationOnOutlined";
import {
  Box,
  CircularProgress,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import {
  useAddressAutocompleteController,
  type AddressResult,
} from "@bw/shared/components";
import { useCallback } from "react";

export interface MaterialAreaCenter {
  label: string;
  lat: number;
  lng: number;
}

export interface MaterialAreaPickerProps {
  onAreaSelect: (area: MaterialAreaCenter) => void;
  value?: string;
  onValueChange?: (value: string) => void;
  defaultValue?: string;
  label?: string;
  placeholder?: string;
  error?: string;
  unavailableHelperText?: string;
  apiKey?: string;
  disabled?: boolean;
  id?: string;
}

/**
 * Player-native MUI renderer for the shared Google Places controller. The
 * shared layer remains authoritative for loading, keyboard behavior, verified
 * place resolution, and address semantics; this component only composes the
 * Player audience presentation and projects a result to a coarse area center.
 */
export function MaterialAreaPicker({
  onAreaSelect,
  value,
  onValueChange,
  defaultValue,
  label = "Area",
  placeholder = "Search a city or area",
  error,
  unavailableHelperText,
  apiKey,
  disabled = false,
  id,
}: MaterialAreaPickerProps) {
  const handleAddressSelect = useCallback(
    (address: AddressResult) => {
      onAreaSelect({
        label: address.formatted_address,
        lat: address.lat,
        lng: address.lng,
      });
    },
    [onAreaSelect],
  );
  const controller = useAddressAutocompleteController({
    onAddressSelect: handleAddressSelect,
    value,
    onValueChange,
    defaultValue,
    label,
    apiKey,
    disabled,
    id,
  });

  const helperText = error
    ? error
    : controller.isAvailable
      ? undefined
      : unavailableHelperText ?? "Search by city above, or use your current location.";

  return (
    <Box sx={{ position: "relative", width: "100%" }}>
      <TextField
        id={controller.inputId}
        inputRef={controller.inputRef}
        fullWidth
        size="small"
        label={label}
        value={controller.value}
        onChange={(event) => controller.setValue(event.target.value)}
        onFocus={controller.handleInputFocus}
        onBlur={controller.handleInputBlur}
        onKeyDown={(event) => void controller.handleInputKeyDown(event)}
        placeholder={
          controller.isAvailable
            ? controller.isLoaded
              ? placeholder
              : "Loading…"
            : "Google address search unavailable"
        }
        disabled={controller.isDisabled}
        error={Boolean(error)}
        helperText={helperText}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <LocationOnOutlined fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: controller.suggestionsLoading ? (
              <InputAdornment position="end">
                <CircularProgress size={18} aria-label="Searching places" />
              </InputAdornment>
            ) : undefined,
          },
          htmlInput: {
            role: "combobox",
            "aria-autocomplete": "list",
            "aria-controls": controller.showSuggestionPanel
              ? controller.listboxId
              : undefined,
            "aria-expanded": controller.showSuggestionPanel,
            "aria-activedescendant":
              controller.activeIndex >= 0
                ? `${controller.listboxId}-option-${controller.activeIndex}`
                : undefined,
          },
        }}
      />

      {controller.showSuggestionPanel &&
      !controller.isDisabled &&
      controller.isLoaded ? (
        <Paper
          variant="outlined"
          sx={{
            position: "absolute",
            insetInline: 0,
            top: "calc(100% + 8px)",
            zIndex: (theme) => theme.zIndex.modal,
            overflow: "hidden",
          }}
        >
          {controller.suggestionsLoading ? (
            <Box sx={{ px: 2, py: 1.5 }} role="status">
              <Typography variant="body2" color="text.secondary">
                Searching Google places…
              </Typography>
            </Box>
          ) : controller.suggestions.length > 0 ? (
            <List
              id={controller.listboxId}
              role="listbox"
              aria-label={`${label} suggestions`}
              dense
              disablePadding
              sx={{ maxHeight: 320, overflowY: "auto", py: 0.5 }}
            >
              {controller.suggestions.map((suggestion, index) => (
                <ListItemButton
                  component="li"
                  id={`${controller.listboxId}-option-${index}`}
                  key={suggestion.place_id}
                  role="option"
                  aria-selected={index === controller.activeIndex}
                  selected={index === controller.activeIndex}
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseEnter={() => controller.handleSuggestionHover(index)}
                  onClick={() =>
                    void controller.handleSuggestionSelect(suggestion)
                  }
                >
                  <ListItemText
                    primary={suggestion.main_text}
                    secondary={suggestion.secondary_text || undefined}
                  />
                </ListItemButton>
              ))}
            </List>
          ) : (
            <Box sx={{ px: 2, py: 1.5 }} role="status">
              <Typography variant="body2" color="text.secondary">
                No matching places yet.
              </Typography>
            </Box>
          )}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", px: 2, py: 1, textAlign: "right" }}
          >
            Powered by Google
          </Typography>
        </Paper>
      ) : null}
    </Box>
  );
}
